import type {
  CampaignCreationCapabilities,
  SmartCreationBrowserDraft,
  SmartCreationReviewAdGroup,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError } from "@/components/ui/field";
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs";
import {
  type NegativeEditorEntry,
  negativeCandidateLabel,
  negativeCandidateMatch,
  ReviewNegativeTargetEditor,
} from "./ReviewNegativeTargetEditor";
import type { ReviewChangeHandler } from "./review-change-handler";
import {
  type AddedNegative,
  type QuickTargetValue,
  quickTargetIdentity,
} from "./review-quick-targets";
import { SelectedTargetsSection } from "./review-target-dialog/SelectedTargetsSection";
import { TargetCandidatesSection } from "./review-target-dialog/TargetCandidatesSection";
import { TargetDialogHeader } from "./review-target-dialog/TargetDialogHeader";
import { buildTargetDialogChanges } from "./review-target-dialog/target-changes";
import {
  type Entry,
  entryIdentity,
  groupLane,
  matchTranslationKey,
  negativeEntry,
  strategyCandidatesByGroup,
  targetEntry,
} from "./review-target-dialog/target-entries";
import { useReviewBidPreview } from "./review-target-dialog/use-review-bid-preview";
import { targetDisplayLabel, useTargetDisplayNames } from "./use-target-display-names";

interface ReviewTargetDialogProps {
  workspace: SmartCreationReviewWorkspace;
  plan?: SmartCreationBrowserDraft;
  capabilities?: CampaignCreationCapabilities;
  group: SmartCreationReviewAdGroup;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onClose(): void;
  onGroupChange?(group: SmartCreationReviewAdGroup): void;
  onDirtyChange?(dirty: boolean): void;
  onEditGroup?(group: SmartCreationReviewAdGroup): void;
}

/** Transfers stay local until one CAS batch is saved; deselected targets keep their staged bid. */
export function ReviewTargetDialog({
  workspace,
  plan,
  capabilities,
  group,
  disabled,
  onApply,
  onClose,
  onGroupChange,
  onDirtyChange,
  onEditGroup,
}: ReviewTargetDialogProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const [baseline] = useState(() => structuredClone(workspace));
  const precise = baseline.suggestedDraft.structure === "precise";
  const [activeGroup, setActiveGroup] = useState(group.ref.lineageKey);
  const [negativeMode, setNegativeMode] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [selectedMatchFilter, setSelectedMatchFilter] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [createdTargets, setCreatedTargets] = useState<Record<string, Entry[]>>({});
  const [retainedTargets, setRetainedTargets] = useState<Record<string, Entry[]>>({});
  const [pendingExit, setPendingExit] = useState<"close" | string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [positiveEntries, setPositiveEntries] = useState<Record<string, Entry[]>>(() =>
    Object.fromEntries(
      baseline.graph.adGroups.map((item) => [
        item.ref.lineageKey,
        baseline.graph.targets
          .filter(
            (target) =>
              target.adGroupLineageKey === item.ref.lineageKey && !target.excluded.effective,
          )
          .map((target) => targetEntry(target, plan)),
      ]),
    ),
  );
  const [negativeEntries, setNegativeEntries] = useState<Record<string, Entry[]>>(() =>
    Object.fromEntries(
      baseline.graph.adGroups.map((item) => [
        item.ref.lineageKey,
        baseline.graph.negatives
          .filter((target) => target.adGroupLineageKey === item.ref.lineageKey)
          .map(negativeEntry),
      ]),
    ),
  );
  const [retainedNegativeEntries, setRetainedNegativeEntries] = useState(negativeEntries);
  const [strategyCandidates] = useState<Record<string, Entry[]>>(() =>
    strategyCandidatesByGroup(baseline, plan, precise),
  );
  const selectedGroup =
    baseline.graph.adGroups.find((item) => item.ref.lineageKey === activeGroup) ?? group;
  const activeLane = groupLane(baseline, selectedGroup);
  const strategy = activeLane.strategy === "automatic" ? "research" : activeLane.strategy;
  const laneGroups = baseline.graph.adGroups;
  const selectedCampaign = baseline.graph.campaigns.find(
    (item) => item.ref.lineageKey === selectedGroup.campaignLineageKey,
  );

  const originals = baseline.graph.targets.filter(
    (target) => target.adGroupLineageKey === activeGroup,
  );
  const entries = (negativeMode ? negativeEntries : positiveEntries)[activeGroup] ?? [];
  const automaticInitialization = workspace.automaticBidInitializations?.find(
    (item) => item.adGroupKey === selectedGroup.ref.itemKey,
  );
  const planId = plan?.id;
  const planShopId = plan?.shopId;
  const { names: categoryNames, loading: categoryNamesLoading } = useTargetDisplayNames(
    planShopId,
    baseline.graph.targets,
  );
  const reviewStatus = plan?.reviewWorkspace?.status;
  const stale = baseline.contentHash !== workspace.contentHash;
  const readOnly = workspace.status === "confirmed";
  const viewLocked = disabled || submitting;
  const mutationDisabled = viewLocked || stale || readOnly;
  const locked = mutationDisabled;
  const changeEntries = (next: Entry[]) => {
    if (mutationDisabled) return;
    setError(null);
    if (negativeMode)
      setRetainedNegativeEntries((current) => ({
        ...current,
        [activeGroup]: [
          ...new Map(
            [...(current[activeGroup] ?? []), ...next].map((entry) => [entry.id, entry]),
          ).values(),
        ],
      }));
    else
      setRetainedTargets((current) => ({
        ...current,
        [activeGroup]: [
          ...new Map(
            [...(current[activeGroup] ?? []), ...next].map((entry) => [entry.id, entry]),
          ).values(),
        ],
      }));
    (negativeMode ? setNegativeEntries : setPositiveEntries)((current) => ({
      ...current,
      [activeGroup]: next,
    }));
  };

  const bidPreview = useReviewBidPreview({
    workspace,
    planId,
    planShopId,
    activeGroup,
    positiveEntries: positiveEntries[activeGroup] ?? [],
    automaticInitialization,
    reviewStatus,
    stale,
    negativeMode,
  });

  const initialEntries = useRef(JSON.stringify({ positiveEntries, negativeEntries }));
  const dirty = initialEntries.current !== JSON.stringify({ positiveEntries, negativeEntries });
  const dirtyCallback = useRef(onDirtyChange);
  dirtyCallback.current = onDirtyChange;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => dirtyCallback.current?.(false), []);
  function closeDetails() {
    dirtyCallback.current?.(false);
    onClose();
  }
  const allEntries = negativeMode
    ? entries
    : [
        ...new Map(
          [
            ...originals.map((target) => targetEntry(target, plan)),
            ...(strategyCandidates[activeGroup] ?? []),
            ...(createdTargets[activeGroup] ?? []),
            ...(retainedTargets[activeGroup] ?? []),
            ...entries,
          ].map((entry) => [entry.id, entry]),
        ).values(),
      ];
  const entryLabel = (entry: Entry) =>
    entry.original?.type === "automatic"
      ? t(`review.preview.graph.expressions.${entry.original.expressionType}`)
      : entry.original
        ? (targetDisplayLabel(entry.original, categoryNames) ?? entry.label)
        : entry.label;
  const matchLabel = (match: string) =>
    match === "product"
      ? t("review.negativeType.product")
      : ["close_match", "loose_match", "substitutes", "complements"].includes(match)
        ? t(`review.preview.graph.expressions.${match}`)
        : t(`review.match.${matchTranslationKey(match)}`, { defaultValue: match });
  const matchItems = [
    { value: "all", label: t("review.targetSelection.allMatches") },
    ...[...new Set(allEntries.map((entry) => entry.match))].map((value) => ({
      value,
      label: matchLabel(value),
    })),
  ];
  const normalizedSearch = search.trim().toLocaleLowerCase(i18n.resolvedLanguage);
  const sourceLabel = (entry: Entry) => t(`review.targetTransfer.sources.${entry.source}`);
  const visibleEntries = allEntries.filter(
    (entry) =>
      (matchFilter === "all" || entry.match === matchFilter) &&
      (!normalizedSearch ||
        `${entryLabel(entry)} ${matchLabel(entry.match)} ${sourceLabel(entry)}`
          .toLocaleLowerCase(i18n.resolvedLanguage)
          .includes(normalizedSearch)),
  );
  const visibleSelectedEntries = entries.filter(
    (entry) =>
      (selectedMatchFilter === "all" || entry.match === selectedMatchFilter) &&
      `${entryLabel(entry)} ${matchLabel(entry.match)} ${sourceLabel(entry)}`
        .toLocaleLowerCase(i18n.resolvedLanguage)
        .includes(selectedSearch.trim().toLocaleLowerCase(i18n.resolvedLanguage)),
  );
  function changeGroup(key: string) {
    setActiveGroup(key);
    setSearch("");
    setSelectedSearch("");
    setSelectedMatchFilter("all");
    setMatchFilter("all");
    setError(null);
    const next = baseline.graph.adGroups.find((item) => item.ref.lineageKey === key);
    if (next) onGroupChange?.(next);
  }
  function requestExit(destination: string) {
    if (disabled || submitLock.current) return;
    if (dirty) setPendingExit(destination);
    else if (destination === "close") closeDetails();
    else changeGroup(destination);
  }
  const picked = (entry: Entry) =>
    entries.some((item) => item.id === entry.id || entryIdentity(item) === entryIdentity(entry));
  const toggle = (entry: Entry, checked: boolean) =>
    changeEntries(
      checked
        ? picked(entry)
          ? entries
          : [...entries, entry]
        : entries.filter(
            (item) => item.id !== entry.id && entryIdentity(item) !== entryIdentity(entry),
          ),
    );

  function removeEntry(entry: Entry) {
    if (locked || !picked(entry)) return;
    changeEntries(entries.filter((item) => item.id !== entry.id));
  }

  function addQuickValues(values: QuickTargetValue[]): {
    added: number;
    skipped: number;
    error?: string;
  } {
    if (mutationDisabled) return { added: 0, skipped: 0, error: t("review.parity.staleTargets") };
    const next = [...entries];
    const identities = new Set(next.map(entryIdentity));
    const created: Entry[] = [];
    let added = 0;
    let skipped = 0;
    for (const value of values) {
      const identity = quickTargetIdentity(
        value.type === "product" && negativeMode ? "product" : value.match,
        value.label,
      );
      if (identities.has(identity)) {
        skipped++;
        continue;
      }
      if (negativeMode) {
        const existing = (retainedNegativeEntries[activeGroup] ?? []).find(
          (entry) => entryIdentity(entry) === identity,
        );
        if (existing) {
          next.push(existing);
          identities.add(identity);
          added++;
          continue;
        }
      }
      if (!negativeMode) {
        // An excluded or removed target still owns its canonical intent on the server.
        const existing = [
          ...(retainedTargets[activeGroup] ?? []),
          ...originals.map((target) => targetEntry(target, plan)),
          ...(strategyCandidates[activeGroup] ?? []),
          ...(createdTargets[activeGroup] ?? []),
        ].find((entry) => entryIdentity(entry) === identity);
        if (existing) {
          next.push(existing);
          identities.add(identity);
          added++;
          continue;
        }
      }
      const id = `quick:${crypto.randomUUID()}`;
      const entry: Entry = {
        id,
        label: value.label,
        match: negativeMode && value.type === "product" ? "product" : value.match,
        bid: String(value.bid),
        source: "user",
      };
      if (negativeMode) {
        entry.negativeAddition =
          value.type === "product"
            ? {
                type: "product",
                ref: { kind: "negative_product", itemKey: null, lineageKey: id },
                asin: value.label,
              }
            : {
                type: "keyword",
                ref: { kind: "negative_keyword", itemKey: null, lineageKey: id },
                text: value.label,
                matchType: value.match === "NEGATIVE_PHRASE" ? "NEGATIVE_PHRASE" : "NEGATIVE_EXACT",
              };
      } else {
        const common = {
          ref: { kind: "target" as const, itemKey: null, lineageKey: id },
          sourceTargetId: id,
          strategyKind: strategy,
          bid: value.bid,
        };
        entry.addition =
          value.type === "product"
            ? {
                ...common,
                type: "product",
                asin: value.label,
                matchType: value.match === "expanded" ? "expanded" : "exact",
              }
            : {
                ...common,
                type: "keyword",
                keyword: value.label,
                matchType:
                  value.match === "phrase" ? "phrase" : value.match === "exact" ? "exact" : "broad",
              };
        created.push(entry);
      }
      next.push(entry);
      identities.add(identity);
      added++;
    }
    const maxKeywords = capabilities?.limits.maxNegativeKeywords ?? 1000;
    const maxProducts = capabilities?.limits.maxNegativeProducts ?? 1000;
    if (
      negativeMode &&
      (next.filter((item) => item.match !== "product").length > maxKeywords ||
        next.filter((item) => item.match === "product").length > maxProducts)
    )
      return { added: 0, skipped, error: t("review.quickAdd.limit") };
    if (!negativeMode && originals.length + next.filter((item) => item.addition).length > 500)
      return { added: 0, skipped, error: t("review.quickAdd.limit") };
    changeEntries(next);
    if (!negativeMode) {
      setCreatedTargets((current) => ({
        ...current,
        [activeGroup]: [...(current[activeGroup] ?? []), ...created],
      }));
    }
    return { added, skipped };
  }

  function changeNegativeEntries(next: NegativeEditorEntry[]) {
    if (mutationDisabled) return;
    if (
      next.filter((entry) => entry.match !== "product").length >
        (capabilities?.limits.maxNegativeKeywords ?? 1000) ||
      next.filter((entry) => entry.match === "product").length >
        (capabilities?.limits.maxNegativeProducts ?? 1000)
    ) {
      setError(t("review.quickAdd.limit"));
      return;
    }
    const retained = retainedNegativeEntries[activeGroup] ?? [];
    changeEntries(
      next.map((item) => {
        const original = retained.find((entry) => entryIdentity(entry) === entryIdentity(item));
        if (original) return original;
        const ref = { itemKey: null, lineageKey: item.id };
        const negativeAddition: AddedNegative =
          item.match === "product"
            ? { type: "product", ref: { ...ref, kind: "negative_product" }, asin: item.label }
            : {
                type: "keyword",
                ref: { ...ref, kind: "negative_keyword" },
                text: item.label,
                matchType: item.match === "NEGATIVE_PHRASE" ? "NEGATIVE_PHRASE" : "NEGATIVE_EXACT",
              };
        return { ...item, bid: "", source: "unknown", negativeAddition };
      }),
    );
  }

  async function confirm(): Promise<void> {
    if (locked || submitLock.current) return;
    const changes = buildTargetDialogChanges(
      baseline,
      precise,
      positiveEntries,
      negativeEntries,
      capabilities?.bid,
    );
    if (!changes) {
      setError(t("review.parity.invalidTargets"));
      return;
    }
    if (changes.length > 100) {
      setError(t("review.parity.tooManyChanges"));
      return;
    }
    if (!changes.length) {
      closeDetails();
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const applied = await onApply(changes);
      if (applied === false) setError(t("review.parity.applyFailed"));
      else closeDetails();
    } catch {
      setError(t("review.parity.applyFailed"));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) requestExit("close");
        }}
      >
        <DialogPopup
          showCloseButton={false}
          bottomStickOnMobile={false}
          viewportClassName="grid-rows-[1fr_auto_1fr] p-3"
          className="smart-creation-preview h-[min(760px,calc(100dvh-24px))] max-w-[1180px] gap-0 overflow-hidden border border-line"
        >
          <TargetDialogHeader
            plan={plan}
            selectedGroup={selectedGroup}
            selectedCampaign={selectedCampaign}
            strategy={activeLane.strategy}
            activeGroup={activeGroup}
            laneGroups={laneGroups}
            campaigns={baseline.graph.campaigns}
            viewLocked={viewLocked}
            locked={locked}
            closeDisabled={disabled || submitting}
            onEditGroup={onEditGroup}
            onRequestExit={requestExit}
          />
          <div className="flex h-10 shrink-0 items-center justify-between gap-3 border-b border-line px-5">
            <Tabs
              value={negativeMode ? "negative" : "target"}
              onValueChange={(value) => {
                setNegativeMode(value === "negative");
                setSearch("");
                setSelectedSearch("");
                setSelectedMatchFilter("all");
                setMatchFilter("all");
                setError(null);
              }}
            >
              <TabsList aria-label={t("review.parity.targetMode")} className="h-10 gap-4 border-0">
                <TabsTab
                  value="target"
                  aria-label={t("review.parity.targets")}
                  disabled={viewLocked}
                  className="h-10 px-0"
                >
                  {t("review.parity.targets")}{" "}
                  <span className="tabular-nums">{positiveEntries[activeGroup]?.length ?? 0}</span>
                </TabsTab>
                <TabsTab
                  value="negative"
                  aria-label={t("review.parity.negativeTargets")}
                  disabled={viewLocked}
                  className="h-10 px-0"
                >
                  {t("review.parity.negativeTargets")}{" "}
                  <span className="tabular-nums">{negativeEntries[activeGroup]?.length ?? 0}</span>
                </TabsTab>
              </TabsList>
            </Tabs>
            <span
              className={`${negativeMode ? "hidden" : "hidden sm:block"} truncate text-[11px] text-muted-foreground`}
            >
              {t("review.targetTransfer.bidHint")}
            </span>
          </div>
          {negativeMode ? (
            <ReviewNegativeTargetEditor
              key={activeGroup}
              shopId={planShopId}
              group={selectedGroup}
              targetAcos={plan?.draft.targetAcos ?? null}
              currencyCode={workspace.summary.currencyCode}
              capabilities={capabilities}
              disabled={locked}
              enabled={!stale}
              entries={negativeEntries[activeGroup] ?? []}
              retainedEntries={retainedNegativeEntries[activeGroup] ?? []}
              positiveEntries={positiveEntries[activeGroup] ?? []}
              onChange={changeNegativeEntries}
              onSelectCandidate={(candidate) =>
                changeNegativeEntries([
                  ...(negativeEntries[activeGroup] ?? []),
                  {
                    id: `negative-candidate:${candidate.id}:${crypto.randomUUID()}`,
                    label: negativeCandidateLabel(candidate),
                    match: negativeCandidateMatch(candidate),
                  },
                ])
              }
              onAdd={addQuickValues}
            />
          ) : (
            <div
              className={`grid min-h-0 flex-1 gap-4 p-4 md:px-6 ${negativeMode ? "grid-cols-1" : "grid-cols-1 grid-rows-2 md:grid-cols-[0.48fr_0.52fr] md:grid-rows-1"}`}
            >
              {!negativeMode ? (
                <TargetCandidatesSection
                  groupKey={activeGroup}
                  lane={activeLane}
                  precise={precise}
                  defaultBid={selectedGroup.defaultBid.effective}
                  bidLimits={capabilities?.bid}
                  locked={locked}
                  candidateCount={allEntries.length}
                  entries={entries}
                  visibleEntries={visibleEntries}
                  search={search}
                  matchFilter={matchFilter}
                  matchItems={matchItems}
                  categoryNamesLoading={categoryNamesLoading}
                  entryLabel={entryLabel}
                  matchLabel={matchLabel}
                  sourceLabel={sourceLabel}
                  picked={picked}
                  onAdd={addQuickValues}
                  onSearchChange={setSearch}
                  onMatchFilterChange={setMatchFilter}
                  onEntriesChange={changeEntries}
                  onToggle={toggle}
                />
              ) : null}
              <SelectedTargetsSection
                negativeMode={negativeMode}
                groupKey={activeGroup}
                lane={activeLane}
                defaultBid={selectedGroup.defaultBid.effective}
                capabilities={capabilities}
                currencyCode={workspace.summary.currencyCode}
                locked={locked}
                entries={entries}
                visibleSelectedEntries={visibleSelectedEntries}
                selectedSearch={selectedSearch}
                selectedMatchFilter={selectedMatchFilter}
                matchItems={matchItems}
                bidPreview={bidPreview}
                automaticInitialization={automaticInitialization}
                entryLabel={entryLabel}
                matchLabel={matchLabel}
                sourceLabel={sourceLabel}
                onAdd={addQuickValues}
                onEntriesChange={changeEntries}
                onSelectedSearchChange={setSelectedSearch}
                onSelectedMatchFilterChange={setSelectedMatchFilter}
                onRemoveEntry={removeEntry}
              />
            </div>
          )}
          {error || stale ? (
            <Field invalid className="shrink-0 px-5 pb-2">
              <FieldError match>{stale ? t("review.parity.staleTargets") : error}</FieldError>
            </Field>
          ) : null}
          <DialogFooter className="h-14 shrink-0 flex-row items-center justify-end gap-2 border-line px-5 py-2">
            <p className="mr-auto hidden text-[11px] text-muted-foreground sm:block">
              {t(
                negativeMode
                  ? "review.negativeTransfer.saveHint"
                  : "review.targetTransfer.saveHint",
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled || submitting}
              onClick={() => requestExit("close")}
            >
              {t("review.parity.cancel")}
            </Button>
            <Button size="sm" disabled={locked} onClick={confirm}>
              {t("review.sheet.save")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
      <Dialog
        open={pendingExit !== null}
        onOpenChange={(open) => {
          if (!open) setPendingExit(null);
        }}
      >
        <DialogPopup className="smart-creation-preview">
          <DialogHeader>
            <DialogTitle>{t("review.sheet.unsavedTitle")}</DialogTitle>
          </DialogHeader>
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            {t("review.sheet.unsavedDescription")}
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPendingExit(null)}>
              {t("review.sheet.keepEditing")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const destination = pendingExit;
                setPendingExit(null);
                if (destination === "close") closeDetails();
                else if (destination) changeGroup(destination);
              }}
            >
              {t("review.sheet.leave")}
            </Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}
